import type {
  GlobalSearchResourceItem,
  GlobalSearchUserResult,
  Office
} from "@gettactic/api";
import { Setting } from "./constants";

type SearchResult = {
  type: "resource" | "person" | "office" | "setting";
  item: GlobalSearchResourceItem | GlobalSearchUserResult | Office | Setting;
  matchScore: number;
};

interface MatchResult {
  matches: boolean;
  score: number;
}

const SCORE_EXACT_MATCH = 100;
const SCORE_STARTS_WITH = 90;
const SCORE_CONTAINS_BASE = 70;
const SCORE_WORD_STARTS_WITH = 60;

function calculateMatchScore(text: string, query: string): MatchResult {
  const lowerText = text.toLowerCase();
  const lowerQuery = query.toLowerCase();

  if (!lowerQuery) {
    return { matches: true, score: 0 };
  }

  if (lowerText === lowerQuery) {
    return { matches: true, score: SCORE_EXACT_MATCH };
  }

  if (lowerText.startsWith(lowerQuery)) {
    return { matches: true, score: SCORE_STARTS_WITH };
  }

  const index = lowerText.indexOf(lowerQuery);
  if (index !== -1) {
    return { matches: true, score: SCORE_CONTAINS_BASE - index };
  }

  const words = lowerText.split(/\s+/);
  for (const word of words) {
    if (word.startsWith(lowerQuery)) {
      return { matches: true, score: SCORE_WORD_STARTS_WITH };
    }
  }

  return { matches: false, score: 0 };
}

type SearchResultType = "resource" | "person" | "office" | "setting";

function createSearchResult<T>(
  type: SearchResultType,
  item: T,
  matchScore: number
): SearchResult {
  return { type, item, matchScore } as SearchResult;
}

function genericSearch<T>(
  items: T[],
  query: string,
  type: SearchResultType,
  scoreFields: (item: T) => number
): SearchResult[] {
  if (!query.trim()) {
    return items.map((item) => createSearchResult(type, item, 0));
  }

  return items
    .map((item) => createSearchResult(type, item, scoreFields(item)))
    .filter((result) => result.matchScore > 0)
    .sort((a, b) => b.matchScore - a.matchScore);
}

export function searchOffices(
  offices: Office[],
  query: string
): SearchResult[] {
  return genericSearch(offices, query, "office", (office) => {
    const scores = [
      calculateMatchScore(office.name, query).score,
      office.city ? calculateMatchScore(office.city, query).score : 0,
      office.state ? calculateMatchScore(office.state, query).score : 0,
      office.address ? calculateMatchScore(office.address, query).score : 0
    ];
    return Math.max(...scores);
  });
}

export function searchSettings(
  settings: Setting[],
  query: string
): SearchResult[] {
  return genericSearch(settings, query, "setting", (setting) => {
    return calculateMatchScore(setting.name, query).score;
  });
}
